import type { FlightState, Bullet, Effect } from "./types";
const bullets = [
  "main",
  "wing",
  "orbit",
  "homing",
  "boomerang",
  "split",
  "arc",
  "fragment",
  "enemy",
  "boss",
  "straight",
  "double",
  "fan",
  "laser",
  "flame",
  "rocket",
  "rail",
  "tesla",
  "pellet",
  "needle",
  "ring",
  "orb",
  "mine",
  "blade",
  "enemy-beam",
  "bounce",
  "missile",
];
const effects = [
  "hit",
  "burst",
  "rescue",
  "ink",
  "chain",
  "evolve",
  "supply",
  "hurt",
  "boss",
] as const;
const n = (v: number) => Math.round(v * 100) / 100;
export interface FlightFrame extends Omit<
  FlightState,
  "bullets" | "enemies" | "effects"
> {
  packed: 1;
  labels?: { id: number; label?: string; target?: string }[];
  bullets: number[][];
  enemies: number[][];
  effects: number[][];
}
/** Visual snapshots omit collision history, unneeded simulation data and repeated field names. */
export function packFlight(s: FlightState): FlightFrame {
  return {
    ...s,
    packed: 1,
    labels: s.effects
      .filter((e) => e.label || e.target)
      .map((e) => ({ id: e.id, label: e.label, target: e.target })),
    bullets: s.bullets.map((b) => [
      b.id,
      s.pilots.findIndex((p) => p.id === b.owner),
      bullets.indexOf(b.kind),
      n(b.x),
      n(b.y),
      n(b.vx),
      n(b.vy),
      b.radius,
      b.life,
      b.warmup ?? 0,
      b.age ?? 0,
      b.turn ?? 0,
    ]),
    enemies: s.enemies.map((e) => [
      e.id,
      e.kind,
      n(e.x),
      n(e.y),
      n(e.hp),
      n(e.maxHp),
      e.age,
      e.boss ? 1 : 0,
      e.radius,
      e.warnAt ?? 0,
      e.aim ?? 0,
      e.elite ? 1 : 0,
      e.slowUntil ?? 0,
      e.altitude === "ground" ? 1 : 0,
    ]),
    effects: s.effects.map((e) => [
      e.id,
      n(e.x),
      n(e.y),
      e.age,
      effects.indexOf(e.kind),
      e.toX ?? e.x,
      e.toY ?? e.y,
    ]),
  };
}
export function unpackFlight(frame: FlightFrame): FlightState {
  return {
    ...frame,
    bullets: frame.bullets.map(
      (b) =>
        ({
          id: b[0],
          owner: frame.pilots[b[1]]?.id ?? "enemy",
          kind: bullets[b[2]],
          x: b[3],
          y: b[4],
          vx: b[5],
          vy: b[6],
          radius: b[7],
          damage: 0,
          life: b[8] ?? 1,
          warmup: b[9] ?? 0,
          age: b[10] ?? 0,
          turn: b[11] ?? 0,
          pierce: 0,
          hits: [],
        }) as Bullet,
    ),
    enemies: frame.enemies.map((e) => ({
      id: e[0],
      kind: e[1],
      x: e[2],
      y: e[3],
      hp: e[4],
      maxHp: e[5],
      age: e[6],
      boss: !!e[7],
      radius: e[8],
      warnAt: e[9] ?? 0,
      aim: e[10] ?? 0,
      elite: !!e[11],
      slowUntil: e[12] ?? 0,
      altitude: e[13] ? ("ground" as const) : ("air" as const),
      fireAt: 0,
      phase: 0,
      scale: 1,
    })),
    effects: frame.effects.map(
      (e) =>
        ({
          id: e[0],
          x: e[1],
          y: e[2],
          age: e[3],
          kind: effects[e[4]],
          toX: e[5],
          toY: e[6],
          label: frame.labels?.find((v) => v.id === e[0])?.label,
          target: frame.labels?.find((v) => v.id === e[0])?.target,
        }) as Effect,
    ),
  };
}
